import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  CircularProgress,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  History as HistoryIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ImportResult, ImportSession } from '../../../types/import.types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`import-tabpanel-${index}`}
      aria-labelledby={`import-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  onDownloadTemplate: () => void;
  onImport: (file: File) => Promise<void>;
  onFetchHistory: () => void;
  onRevert: (session: ImportSession) => void;
  history: ImportSession[];
  importResult: ImportResult | null;
  isLoading: boolean;
  isHistoryLoading: boolean;
  entityName: string; // e.g., "продуктів", "препаратів"
}

export const ImportDialog: React.FC<ImportDialogProps> = ({
  open,
  onClose,
  onDownloadTemplate,
  onImport,
  onFetchHistory,
  onRevert,
  history,
  importResult,
  isLoading,
  isHistoryLoading,
  entityName,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [file, setFile] = useState<File | null>(null);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 1) {
      onFetchHistory();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
    }
  };

  const handleImportClick = async () => {
    if (file) {
      await onImport(file);
      setFile(null); // Clear file after import attempt
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Імпорт {entityName} з Excel</DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="import tabs">
            <Tab label="Новий імпорт" icon={<UploadIcon />} iconPosition="start" />
            <Tab label="Історія" icon={<HistoryIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ pt: 1 }}>
            <Typography paragraph>
              Завантажте шаблон, заповніть його даними та завантажте назад у систему.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={onDownloadTemplate}
              sx={{ mb: 3 }}
            >
              Завантажити шаблон (.xlsx)
            </Button>

            <Box sx={{ mb: 2 }}>
              <input
                accept=".xlsx, .xls"
                style={{ display: 'none' }}
                id="raised-button-file"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="raised-button-file">
                <Button variant="contained" component="span" startIcon={<UploadIcon />}>
                  Вибрати файл
                </Button>
              </label>
              {file && <Typography variant="body2" sx={{ mt: 1 }}>Вибрано: {file.name}</Typography>}
            </Box>

            {importResult && (
              <Box sx={{ mt: 2 }}>
                <Alert severity={importResult.errors.length > 0 ? "warning" : "success"}>
                  Успішно імпортовано: {importResult.count} (Сесія #{importResult.sessionId})
                </Alert>
                {importResult.errors.length > 0 && (
                  <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <List dense>
                      {importResult.errors.map((err, index) => (
                        <ListItem key={index}>
                          <ListItemText primary={err} primaryTypographyProps={{ color: 'error', variant: 'body2' }} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {isHistoryLoading ? (
            <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
          ) : history.length === 0 ? (
            <Typography align="center" sx={{ mt: 2 }}>Історія порожня</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Дата</TableCell>
                    <TableCell>Файл</TableCell>
                    <TableCell>Створено</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell align="right">Дії</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((session) => (
                    <TableRow key={session.id}>
                      <TableCell>{session.id}</TableCell>
                      <TableCell>{format(new Date(session.createdAt), 'dd.MM.yyyy HH:mm')}</TableCell>
                      <TableCell>{session.fileName || 'N/A'}</TableCell>
                      <TableCell>{session.importedCount}</TableCell>
                      <TableCell>{session.status}</TableCell>
                      <TableCell align="right">
                        {session.status !== 'reverted' && (
                          <Tooltip title="Скасувати імпорт (видалити всі записи)">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => onRevert(session)}
                            >
                              <RestoreIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрити</Button>
        {tabValue === 0 && (
          <Button
            onClick={handleImportClick}
            disabled={!file || isLoading}
            variant="contained"
            color="primary"
          >
            {isLoading ? <CircularProgress size={24} /> : 'Імпортувати'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
